(ns practitest.frontend.forms3.run.mandatory-fields
  (:require [ajax.core :as ajax]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.common.re-frame :refer [partial-dispatch partial-dispatch-sync partial-subscribe]]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.modal :as modal]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.entity-form.forms :as forms]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.forms3.entity-form.fields :as fields3]
            [practitest.frontend.forms3.entity-form.footer :as footer]
            [practitest.frontend.forms3.run.events :as run-events]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [re-frame.core :as re-frame]))

;; Whenever a Run form is loaded - regardless of how you got there (created via
;; the Instance form's Run/Save and Run buttons, the Retest action, a grid row,
;; a direct link) - if the run has mandatory custom fields that are still blank,
;; prompt to fill them in via this modal and push the values with a direct
;; `qtest_runs#update` PUT.

(def form-id :run-mandatory-fields)
(def ^:private modal-state-key :run-mandatory-fields-modal)

(defn- blank-value? [v]
  (or (nil? v)
      (= v "")
      (and (coll? v) (empty? v))))

(defn- blank-linkedlist-parent-ids
  "A Linked List field's available options are derived from its parent
  field's value, so the parent must also be presented in the modal -
  even when it isn't itself mandatory - whenever it's blank on the run.
  Recurses in case the parent is itself a Linked List field."
  [all-fields run-entity-fields run field-id]
  (let [field-spec (get all-fields field-id)]
    (when (= :linkedlist (:type field-spec))
      (when-let [parent-id (:parent_list_field_name field-spec)]
        (when (and (contains? run-entity-fields parent-id)
                   (blank-value? (get run parent-id)))
          (cons parent-id
                (blank-linkedlist-parent-ids all-fields run-entity-fields run parent-id)))))))

(defn missing-fields
  "Ids (keywords) of Run custom fields that are mandatory and currently blank
  on the given run entity, plus any blank parent fields needed to populate a
  mandatory Linked List field's available values."
  [all-fields entity-fields run]
  (let [run-entity-fields (get entity-fields :run)
        mandatory-blank (->> run-entity-fields
                             (filter (fn [[field-id props]] (and (:mandatory? props) (blank-value? (get run field-id)))))
                             (map key))
        parent-ids (mapcat (partial blank-linkedlist-parent-ids all-fields run-entity-fields run)
                            mandatory-blank)]
    (->> (distinct (concat mandatory-blank parent-ids))
         (sort-by #(get-in run-entity-fields [% :order])))))

(re-frame/reg-sub
  ::missing-fields
  (fn [_]
    [(re-frame/subscribe [::forms/base-entity :run])
     (re-frame/subscribe [::subs/all-entity-fields])
     (re-frame/subscribe [::subs/fields])])
  (fn [[run entity-fields all-fields] _]
    ;; :type is only stamped once the full entity has been fetched (see
    ;; forms3.entity-form/got-entity) - :current can otherwise briefly hold
    ;; just {:id run-id} (see subs/set-current-entity-id), which would
    ;; otherwise look like every mandatory field on the run is blank.
    (when (:type run)
      (missing-fields all-fields entity-fields run))))

(def mandatory-fields-form-props
  {:get-field-props (partial-subscribe [::forms/field-props :run])
   :get-form-state  (partial-subscribe [::forms/form-state form-id])
   :set-form-state  (partial-dispatch [::forms/set-form-state form-id])

   :get-field-state (partial-subscribe [::forms/field-state form-id])
   :set-field-state (partial-dispatch-sync [::forms/set-field-state form-id])

   :form-ref (atom nil)})

(re-frame/reg-event-db
  ::show-modal
  (fn [db [_ field-ids run-id]]
    (-> db
        (assoc-in [:form-states2 form-id]
                  {:fields (into {} (map (fn [field-id] [field-id {:value nil :dirty? false}])) field-ids)})
        (assoc-in [modal-state-key :show?] true)
        (assoc-in [modal-state-key :field-ids] field-ids)
        (assoc-in [modal-state-key :run-id] run-id))))

(re-frame/reg-event-db
  ::hide-modal
  (fn [db _]
    (update db modal-state-key dissoc :show?)))

(re-frame/reg-sub
  ::show-modal?
  (fn [db _] (get-in db [modal-state-key :show?])))

(re-frame/reg-sub
  ::field-ids
  (fn [db _] (get-in db [modal-state-key :field-ids] [])))

(re-frame/reg-sub
  ::applying?
  (fn [db _] (contains? (:progress db) ::apply)))

(re-frame/reg-event-fx
  ::submit
  (fn [{db :db} _]
    (if (footer/validate-fields! mandatory-fields-form-props)
      (let [run-id (get-in db [modal-state-key :run-id])
            all-fields (:fields db)
            field-values (into {}
                               (keep (fn [[field-id field-state]]
                                       (let [value (:value field-state)
                                             field-type (:type (get all-fields field-id))]
                                         (when-not (blank-value? value)
                                           [field-id
                                            ;; Backend expects a stringified user id for :user
                                            ;; type custom fields, not the whole user map that
                                            ;; build-field's selector stores as the value.
                                            (if (= :user field-type)
                                              (str (or (:id value) value))
                                              value)]))))
                               (get-in db [:form-states2 form-id :fields]))]
        {:db (update db :progress conj ::apply)
         :http-xhrio {:method :put
                      :uri (broutes/get-url "qtest_runs#update" {:p_id (subs/get-current-project-id db)
                                                                 :id run-id})
                      :params {:custom field-values}
                      :format (ajax/json-request-format)
                      :response-format (ajax/json-response-format {:keywords? true})
                      :on-success [::apply-success run-id]
                      :on-failure [::apply-failure]}})
      {})))

(re-frame/reg-event-fx
  ::apply-success
  (fn [{db :db} [_ run-id _response]]
    {:db (update db :progress disj ::apply)
     :dispatch-n [[::hide-modal]
                  [::run-events/refresh-run run-id]]}))

(re-frame/reg-event-fx
  ::apply-failure
  (fn [{db :db} [_ error]]
    (let [error-response (:response error)
          error-message (or (:error error-response)
                            (first (:errors error-response))
                            (get-translation "run.set-mandatory-fields.error"
                                             "Error updating mandatory fields."))]
      {:db (update db :progress disj ::apply)
       :dispatch [::notifications/error2 error-message {:auto-close true}]})))

(defn set-mandatory-fields-modal []
  (let [show? @(re-frame/subscribe [::show-modal?])
        field-ids @(re-frame/subscribe [::field-ids])
        applying? @(re-frame/subscribe [::applying?])]
    [modal/bootstrap-modal
     {:is-open?         show?
      :on-request-close #(re-frame/dispatch [::hide-modal])
      :close-button?    false
      :dismissible?     false
      :centered?        true
      :class            "set-mandatory-fields-modal"}

     [:div.set-params-header
      [:div.set-params-header__title
       [:span [icons/mux-pencil]]
       (get-translation "run.set-mandatory-fields.title" "Set Mandatory Fields")]
      [:p.text-center.mb-0
       (get-translation "run.set-mandatory-fields.subtitle-line1"
                        "Several mandatory fields were found.")
       [:br]
       (get-translation "run.set-mandatory-fields.subtitle-line2"
                        "Please update the fields first before running the test")]]

     [:div
      (for [field-id field-ids]
        ^{:key field-id}
        [fields3/build-field
         {:field-name field-id
          :edit-permissions? true}
         mandatory-fields-form-props])]

     [:div.d-flex.justify-content-center.gap-3
      [buttons/button
       {:on-click  #(re-frame/dispatch [::submit])
        :primary?  true
        :disabled? applying?
        :icon      (when applying? [spinners/mux-spinner {:size :small}])
        :text      (get-translation "generic.button.update" "Update")}]]]))
